import { onBeforeUnmount, ref, watch } from 'vue'

export interface IUseResizablePaneParams {
  initial: number
  min: number
  max: number | (() => number)
  storageKey?: string
}

export interface IUseResizablePaneReturn {
  width: ReturnType<typeof ref<number>>
  startResize: (event: MouseEvent) => void
  isResizing: ReturnType<typeof ref<boolean>>
}

const LS_PREFIX = 'materio.pane-width.'

function resolveMax(max: number | (() => number)): number {
  return typeof max === 'function' ? max() : max
}

function readFromStorage(key: string | undefined): number | null {
  if (!key) return null
  try {
    const raw = localStorage.getItem(LS_PREFIX + key)
    if (raw == null) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

function writeToStorage(key: string | undefined, value: number): void {
  if (!key) return
  try {
    localStorage.setItem(LS_PREFIX + key, String(Math.round(value)))
  } catch {
    // localStorage unavailable
  }
}

export function useResizablePane(params: IUseResizablePaneParams): IUseResizablePaneReturn {
  const clamp = (v: number) => {
    const maxV = resolveMax(params.max)
    return Math.min(Math.max(v, params.min), maxV)
  }

  const initial = readFromStorage(params.storageKey) ?? params.initial
  const width = ref<number>(clamp(initial))
  const isResizing = ref(false)

  watch(width, v => {
    if (v != null) writeToStorage(params.storageKey, v)
  })

  let startX = 0
  let startWidth = 0

  const onMouseMove = (e: MouseEvent) => {
    const delta = startX - e.clientX
    width.value = clamp(startWidth + delta)
  }

  const onMouseUp = () => {
    isResizing.value = false
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }

  const startResize = (event: MouseEvent) => {
    event.preventDefault()
    startX = event.clientX
    startWidth = width.value ?? params.initial
    isResizing.value = true
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  onBeforeUnmount(() => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  })

  return { width, startResize, isResizing }
}
